<template>
  <!-- Projects and tasks (Workspace369 lib/view/projects/, lib/view/task/): the project header tiles
       (widgets/project_quick_actions.dart) and Financials (budgetUtilization, hoursUtilization,
       hoursTrackedSummary), the task board in Kanban View with the default To-do / In Progress / Done
       columns (config/default_board_config.dart; card from widgets/task_card_widget.dart) and the
       Gantt View (widgets/task_gantt_view.dart: Week zoom, Critical path, milestones as diamonds,
       Finish to start links). Strings are the app's (app_en.arb progressLabel, tasksCompletedOfTotal,
       percentUsed, kanbanView, ganttView, ganttCriticalPathLabel, addTask). Projects, tasks and people
       are sample data. States:
         project  the project header and financials
         board    the project's tasks on the kanban board
         gantt    the same tasks on the Gantt, critical path on -->
  <StoryAppWindow title="Brand identity" :icon="icons.folder">
    <template #actions>
      <em class="saw-tag saw-tag--blue">In Progress</em>
    </template>

    <!-- Project -->
    <section class="saw-layer prs-project" :class="{ 'is-on': state === 'project' }">
      <p class="prs-client saw-muted">PRJ-0007</p>
      <div class="prs-tiles">
        <div class="saw-card"><small>Progress</small><b class="saw-num">58%</b><span>7/12 tasks</span><i class="prs-bar" style="--v: 58%" /></div>
        <div class="saw-card"><small>Budget</small><b class="saw-num prs-left">$1,596.00</b><span>62% used</span><i class="prs-bar" style="--v: 62%" /></div>
      </div>
      <div class="saw-card prs-fin">
        <b class="prs-fin-title">Financials</b>
        <div class="prs-row"><span>Budget Utilization</span><em class="prs-badge">62%</em></div>
        <i class="prs-bar" style="--v: 62%" />
        <div class="prs-row prs-stat"><span>Spent</span><b class="saw-num">$2,604.00</b></div>
        <div class="prs-row"><span>Hours Utilization</span><em class="prs-badge prs-badge--green">71%</em></div>
        <i class="prs-bar prs-bar--hours" style="--v: 71%" />
        <small class="saw-muted">28.5h of 40h tracked (71%) • 11.5h remaining</small>
      </div>
    </section>

    <!-- Board -->
    <section class="saw-layer prs-board-l" :class="{ 'is-on': state === 'board' }">
      <div class="prs-views"><span class="is-on">Kanban View</span><span>List View</span><span>Gantt View</span></div>
      <div class="prs-board">
        <div v-for="col in columns" :key="col.name" class="prs-col">
          <div class="prs-col-head"><em class="saw-tag" :style="{ '--tone': col.tone }">{{ col.name }}</em><small class="saw-muted">{{ col.cards.length }}</small><span class="prs-plus">+</span></div>
          <div v-for="c in col.cards" :key="c.title" class="saw-card prs-card">
            <b>{{ c.title }}</b>
            <span v-if="c.tag" class="prs-tag">{{ c.tag }}</span>
            <span class="prs-foot">
              <small class="saw-muted">{{ c.due }}</small>
              <small v-if="c.sub" class="prs-ring saw-muted">{{ c.sub }}</small>
              <i class="prs-av" :style="{ '--tone': c.tone }">{{ c.who }}</i>
            </span>
          </div>
        </div>
      </div>
    </section>

    <!-- Gantt -->
    <section class="saw-layer prs-gantt-l" :class="{ 'is-on': state === 'gantt' }">
      <div class="prs-views"><span>Kanban View</span><span>List View</span><span class="is-on">Gantt View</span><span class="prs-crit">Critical path</span></div>
      <div class="saw-card prs-gantt">
        <div class="prs-g-head"><span /><span v-for="w in weeks" :key="w">{{ w }}</span></div>
        <div class="prs-g-rows">
          <div v-for="t in gantt" :key="t.title" class="prs-g-row">
            <span class="prs-g-name">{{ t.title }}</span>
            <span class="prs-g-track">
              <i v-if="t.milestone" class="prs-diamond" :style="{ left: `${t.start}%` }" />
              <i v-else class="prs-g-bar" :class="{ 'is-crit': t.crit }" :style="{ left: `${t.start}%`, width: `${t.len}%` }" />
            </span>
          </div>
          <svg class="prs-links" :viewBox="`0 0 100 ${gantt.length * 36}`" preserveAspectRatio="none" aria-hidden="true">
            <path v-for="(l, i) in links" :key="i" :d="l" />
          </svg>
        </div>
      </div>
      <small class="saw-muted prs-legend">Finish to start · drag a bar to move it</small>
    </section>
  </StoryAppWindow>
</template>

<script setup>
defineProps({
  state: { type: String, default: "gantt" },
});

const columns = [
  { name: "To-do", tone: "#6B7280", cards: [
    { title: "Guidelines PDF", tag: "Delivery", due: "Nov 13", who: "MO", tone: "#3f58bf" },
    { title: "Social media kit", due: "Nov 13", sub: "0/3", who: "LP", tone: "#2d7a4e" },
  ] },
  { name: "In Progress", tone: "#3B82F6", cards: [
    { title: "Logo revisions", tag: "Design", due: "Nov 06", sub: "2/4", who: "MO", tone: "#3f58bf" },
    { title: "Color palette", due: "Nov 06", who: "SR", tone: "#ad4e00" },
  ] },
  { name: "Done", tone: "#22C55E", cards: [
    { title: "Moodboard", due: "Oct 09", sub: "3/3", who: "LP", tone: "#2d7a4e" },
    { title: "Logo concepts", due: "Oct 23", who: "MO", tone: "#3f58bf" },
  ] },
];

const weeks = ["Oct 5", "Oct 12", "Oct 19", "Oct 26", "Nov 2", "Nov 9"];
// Days from Oct 5 across the 6-week track, as % (one day is 100/42).
const day = (d) => +((d * 100) / 42).toFixed(2);
const gantt = [
  { title: "Moodboard", start: day(0), len: day(5), crit: true },
  { title: "Logo concepts", start: day(7), len: day(12), crit: true },
  { title: "Logo revisions", start: day(21), len: day(12), crit: true },
  { title: "Color palette", start: day(23), len: day(10) },
  { title: "Guidelines PDF", start: day(34), len: day(5), crit: true },
  { title: "Final handoff", start: day(39), milestone: true },
];

const icons = {
  folder: "M10 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V8c0-1.1-.9-2-2-2h-8l-2-2z",
};
// Finish-to-start links drawn over the rows (x in % of the row, where the track starts at 36%;
// y in px, rows are 36px tall with the bar centred).
const tx = (p) => 36 + p * 0.64;
const rowY = (i) => 18 + i * 36;
const links = [[0, 1], [1, 2], [2, 4], [4, 5]].map(([a, b]) => {
  const from = gantt[a];
  const to = gantt[b];
  const x1 = tx(from.start + from.len);
  const x2 = tx(to.start);
  return `M${x1} ${rowY(a)} H${Math.max(x1, x2) - 0.6} V${rowY(b)} H${x2}`;
});
</script>

<style scoped>
.prs-client { margin: -2px 0 0; font-size: 11.5px; }
.prs-tiles { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 6px; }
.prs-tiles > div { display: grid; gap: 2px; padding: 9px 11px; }
.prs-tiles small, .prs-tiles span { color: var(--saw-muted); font-size: 11px; }
.prs-tiles b { font-size: 16px; text-align: left; }
.prs-bar { display: block; height: 6px; margin-top: 4px; border-radius: 3px; background: linear-gradient(to right, var(--saw-blue) var(--v), var(--saw-wash) var(--v)); }
.prs-bar--hours { background: linear-gradient(to right, var(--saw-green) var(--v), var(--saw-wash) var(--v)); }
.prs-fin { display: grid; gap: 4px; padding: 11px 12px; }
.prs-fin-title { margin-bottom: 4px; font-size: 12.5px; }
.prs-row { display: flex; justify-content: space-between; gap: 8px; margin-top: 6px; font-size: 12px; }
.prs-fin small { font-size: 11px; }
.prs-tiles b.prs-left { color: var(--saw-green); }
.prs-badge { border-radius: 4px; background: var(--saw-blue-fill, var(--saw-wash)); color: var(--saw-blue); padding: 1px 6px; font-size: 11px; font-style: normal; font-weight: 700; }
.prs-badge--green { background: var(--saw-green-fill, var(--saw-wash)); color: var(--saw-green); }
.prs-stat { margin-top: 2px; color: var(--saw-muted); }

.prs-views { display: flex; flex-wrap: wrap; align-items: center; gap: 4px; font-size: 11.5px; }
.prs-views span { border-radius: 5px; color: var(--saw-muted); padding: 4px 8px; }
.prs-views span.is-on { background: var(--saw-card); box-shadow: 0 0 0 1px var(--saw-line); color: var(--saw-text); font-weight: 600; }
.prs-views .prs-crit { margin-left: auto; background: var(--saw-red-fill); color: var(--saw-red); font-weight: 600; }

.prs-board { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 8px; min-height: 0; }
.prs-col { display: grid; align-content: start; gap: 6px; min-width: 0; border-radius: 8px; background: var(--saw-wash); padding: 7px; }
.prs-col-head { display: flex; align-items: center; gap: 6px; font-size: 11px; }
.prs-col-head .saw-tag { background: color-mix(in srgb, var(--tone) 16%, transparent); color: var(--tone); }
.prs-plus { margin-left: auto; color: var(--saw-muted); }
.prs-card { display: grid; gap: 5px; padding: 8px 9px; }
.prs-card b { font-size: 11.5px; line-height: 1.3; }
.prs-tag { justify-self: start; border-radius: 4px; background: var(--saw-wash); padding: 1px 6px; font-size: 10px; }
.prs-foot { display: flex; align-items: center; gap: 6px; }
.prs-foot small { font-size: 10px; }
.prs-av { display: grid; place-items: center; width: 18px; height: 18px; margin-left: auto; border-radius: 50%; background: var(--tone); color: #fff; font-size: 8px; font-style: normal; font-weight: 700; }
.saw-layer.is-on .prs-col:nth-child(2) .prs-card:first-of-type { animation: prs-in 0.5s cubic-bezier(0.33, 1, 0.68, 1) 0.3s both; }
@keyframes prs-in { from { opacity: 0; transform: translateY(-6px); } to { opacity: 1; transform: none; } }

.prs-gantt { position: relative; overflow: hidden; }
.prs-g-head, .prs-g-row { display: grid; grid-template-columns: 36% 1fr; }
.prs-g-head { grid-template-columns: 36% repeat(6, minmax(0, 1fr)); border-bottom: 1px solid var(--saw-line); color: var(--saw-muted); font-size: 9.5px; }
.prs-g-head span { padding: 6px 3px; }
.prs-g-row { align-items: center; height: 36px; border-bottom: 1px solid var(--saw-line); }
.prs-g-name { overflow: hidden; padding: 0 8px 0 10px; font-size: 11px; text-overflow: ellipsis; white-space: nowrap; }
.prs-g-track { position: relative; height: 100%; }
.prs-g-bar { position: absolute; top: 11px; height: 14px; border-radius: 4px; background: var(--saw-blue); opacity: 0.85; transform-origin: left; }
.prs-g-bar.is-crit { background: var(--saw-red); }
.saw-layer.is-on .prs-g-bar { animation: prs-grow 0.6s cubic-bezier(0.33, 1, 0.68, 1) both; }
@keyframes prs-grow { from { transform: scaleX(0); } to { transform: none; } }
.prs-diamond { position: absolute; top: 11px; width: 13px; height: 13px; background: var(--saw-ink); transform: translateX(-50%) rotate(45deg); border-radius: 2px; }
.prs-g-rows { position: relative; }
.prs-links { position: absolute; inset: 0; width: 100%; height: 100%; pointer-events: none; }
.prs-links path { fill: none; stroke: var(--saw-muted); stroke-width: 0.35; vector-effect: non-scaling-stroke; stroke-width: 1.2px; }
.prs-legend { font-size: 10.5px; }

@media (max-width: 639px) {
  .prs-board { grid-template-columns: repeat(2, minmax(0, 1fr)); }
  .prs-col:nth-child(3) { display: none; }
  .prs-g-head span:nth-child(odd):not(:first-child) { visibility: hidden; }
}
@media (prefers-reduced-motion: reduce) {
  .saw-layer.is-on .prs-g-bar, .saw-layer.is-on .prs-col:nth-child(2) .prs-card:first-of-type { animation: none; }
}
</style>
